import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { RawFormFillerData, FormField } from 'shared-utils/types/formFiller';
import { FormFieldOverlay } from './FormFieldOverlay';
import { PDFDisplay } from './PDFDisplay';
import { getHiddenConditionalFields, getSignedFields } from '../../lib/formFiller';
import { debounce } from 'lodash';
import type { LoadedInitialData, CheckboxSubmitData } from './FormFillerProvider';
import { useFormFillerRole, useFormFillerSave, useFormFillerSubmit } from './hooks';
import { scrollIntoView } from '../../lib/scrollIntoView';
import { PageOverlay } from './styles';
import { FormFillerInProgressHeader } from './FormFillerInProgressHeader';
import { breakpoints } from 'design-system/src/tokens/breakpoints';
import * as pdfjsLib from 'pdfjs-dist';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';

// PDF.js worker setup - use CDN to ensure correct version
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).href;

const SidebarWrapper = styled.div`
  flex-shrink: 0;
  align-items: stretch;
  display: flex;
  flex-direction: column;
`;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 1;
  min-height: 0;
  width: 100%;
`;

const InnerContainer = styled.div<{ $hasSidebar: boolean }>`
  position: relative;
  flex-grow: 1;
  flex-shrink: 1;
  min-height: 0;
  display: flex;
  width: 100%;

  @media (max-width: ${breakpoints.tabletMax}px) {
    // Hide sidebar on smaller screens
    ${SidebarWrapper} {
      display: none;
    }
  }
`;

const SAVE_DEBOUNCE_MS = 1000;
const MAX_RETRY_ATTEMPTS = 3;
const INITIAL_RETRY_DELAY_MS = 500;

/**
 * PDFFormDisplay component that renders a PDF with overlaid form fields
 */
export const PDFDisplayWithOverlay: React.FC<{
  initialData: LoadedInitialData;
  sidebar: React.ReactNode;
}> = ({ initialData, sidebar }) => {
  const role = useFormFillerRole();
  const [pageCount, setPageCount] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);

  const formState = useForm<RawFormFillerData>({
    defaultValues: initialData.initialAnswers,
    mode: 'onChange',
  });
  const { register, handleSubmit, setValue } = formState;
  const resetForm = formState.reset;

  const formData = formState.watch();
  const { isDirty, errors } = formState.formState;

  const formFillerSave = useFormFillerSave();
  const [saveStatus, setSaveStatus] = useState<'saving' | 'saved' | 'failed'>('saved');
  const formFillerSubmit = useFormFillerSubmit();
  /**
   * This implements auto-saving.
   *
   * These are some important behaviors:
   * 1. Debounce edits (i.e. don't make a network request for every keystroke)
   * 2. Have the `isDirty` form state reflect whether the form values differ from what we've saved with `formFillerSave`
   * 3. Don't retry in a tight loop if the save function fails -- since this gets triggered whenever the form is changed,
   * `useMutation` retries are not enough, so we implement our own retries with backoff + block on the save state going back
   * to `saved` before disaptching new saves.
   */

  const saveWithRetry = useCallback(
    async (attempt: number = 0): Promise<void> => {
      try {
        const data = formState.getValues();
        await formFillerSave(data);
        const currentData = formState.getValues();
        if (JSON.stringify(data) === JSON.stringify(currentData)) {
          // If the data hasn't changed, reset it so the form `isDirty` is no longer true.
          // Otherwise, this'll get reset on the next save.
          // The data might be different if the user edited the form while the save was in progress.
          resetForm(currentData);
        }
        setSaveStatus('saved');
      } catch (error) {
        if (attempt < MAX_RETRY_ATTEMPTS - 1) {
          const delay = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt);

          await new Promise((resolve) => setTimeout(resolve, delay));
          await saveWithRetry(attempt + 1);
        } else {
          console.error('Max retry attempts reached. Save failed permanently.');
          setSaveStatus('failed'); // Reset status even on failure to allow future saves
        }
      }
    },
    [formFillerSave, resetForm, formState],
  );
  const debouncedSave = useMemo(
    () =>
      debounce(async () => {
        await saveWithRetry();
      }, SAVE_DEBOUNCE_MS),
    [saveWithRetry],
  );

  useEffect(() => {
    if (saveStatus === 'failed') {
      // The save failed across all retries, so stop trying to save
      return;
    }
    if (saveStatus === 'saving') {
      // Let the current save go through.
      // A new save will get triggered once we switch to `saved`
      return;
    }
    if (isDirty) {
      setSaveStatus('saving');
      debouncedSave();
    }
  }, [debouncedSave, saveStatus, isDirty]);

  // Prevent closing the tab when there are unsaved changes
  useEffect(() => {
    const listener = (event: BeforeUnloadEvent) => {
      if (saveStatus !== 'saved') {
        event.preventDefault();
        // Support for legacy browsers
        event.returnValue = true;
        return true;
      }
    };
    window.addEventListener('beforeunload', listener);
    return () => {
      window.removeEventListener('beforeunload', listener);
    };
  }, [saveStatus]);

  const hiddenConditionalFields = useMemo(() => {
    return getHiddenConditionalFields(initialData, formData);
  }, [initialData, formData]);

  const scrollAndFocusField = useCallback((field: FormField) => {
    const container = containerRef.current;
    if (!container) return;

    // Find the field element and the scrollable container
    const fieldElement = container.querySelector(`[data-field-id="${field.id}"]`) as HTMLElement;
    const scrollableContainer = container.querySelector('[data-pdf-scroll-container="true"]') as HTMLElement;
    // Note: ideally we'd be able to cancel this if the component unmounts, but instead we'll tolerate slight
    // jank if the component unmounts in the middle
    scrollIntoView(fieldElement, scrollableContainer, /* focusInput */ !field.isReadOnly);
  }, []);

  const handleSubmitClick = async (checkboxData?: CheckboxSubmitData) => {
    await handleSubmit((data) => formFillerSubmit(data, checkboxData))();
  };

  // Helper function to get the correct value for a field (handles radio button groups)
  const getFieldValue = useCallback((field: FormField, formData: RawFormFillerData) => {
    if (field.type === 'radio' && field.group) {
      // For radio buttons, return the group's selected value
      return formData[field.group];
    }
    return formData[field.id];
  }, []);

  // Helper function to get the correct error for a field (handles radio button groups)
  const getFieldError = useCallback((field: FormField, errors: any) => {
    if (field.type === 'radio' && field.group) {
      // For radio buttons, return the group's error
      return errors[field.group]?.message;
    }
    return errors[field.id]?.message;
  }, []);
  const signedFields = useMemo(() => {
    return getSignedFields({ formFields: initialData.formFields, data: formData, role: role });
  }, [initialData.formFields, formData, role]);

  // Group fields by page
  const fieldsByPage = useMemo(() => {
    const grouped: { [page: number]: FormField[] } = {};

    initialData.formFields.forEach((field) => {
      const page = field.coordinates.page || 0;
      if (!grouped[page]) {
        grouped[page] = [];
      }
      grouped[page].push(field);
    });
    return grouped;
  }, [initialData.formFields]);

  const renderOverlay = useCallback(
    (pageIndex: number) => {
      const pageFields = fieldsByPage[pageIndex] || [];
      return (
        <PageOverlay>
          {pageFields
            .filter((field) => !hiddenConditionalFields.has(field.id))
            .map((field) => (
              <FormFieldOverlay
                key={field.id}
                field={field}
                value={getFieldValue(field, formData)}
                error={getFieldError(field, errors)}
                register={register}
                setValue={setValue}
                data-field-id={field.id}
                signedFields={signedFields}
              />
            ))}
        </PageOverlay>
      );
    },
    [
      fieldsByPage,
      formData,
      errors,
      register,
      setValue,
      getFieldValue,
      getFieldError,
      hiddenConditionalFields,
      signedFields,
    ],
  );
  if (saveStatus === 'failed') {
    return (
      <Container ref={containerRef}>
        <FormFillerInProgressHeader
          formData={formData}
          onFocusField={scrollAndFocusField}
          onSubmit={handleSubmitClick}
          saveStatus={saveStatus}
        />
        <PageLoadError />
      </Container>
    );
  }

  return (
    <Container ref={containerRef}>
      <FormFillerInProgressHeader
        formData={formData}
        onFocusField={scrollAndFocusField}
        onSubmit={handleSubmitClick}
        saveStatus={saveStatus}
      />
      <InnerContainer $hasSidebar={!!sidebar}>
        <PDFDisplay
          basePdfUrl={initialData.basePdfUrl}
          renderOverlay={renderOverlay}
          pageCount={pageCount}
          setPageCount={setPageCount}
          pdfjsLib={pdfjsLib}
        />
        <SidebarWrapper>{sidebar}</SidebarWrapper>
      </InnerContainer>
    </Container>
  );
};
